import {
    FormGroupArray, FormGroupArrayAction, FormGroupArrayInstance, FormGroupArrayInstanceProps,
    FormGroupArrayAddItemProps, FormGroupDeleteConfirmationTextProps,
    FormGroupProps, FormItem, FormItemType, TextInput, UseFormEvent, isMobileView,
    FormItemRule, FormErrors, ActionIconButton
} from "@sis/react-library"
import { CompanyCarLeasesDto } from './FormSamplesFormGroupsPage'
import { Box, Paper, Stack, useTheme } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import ArrowCircleDownIcon from '@mui/icons-material/ArrowCircleDown';
import { PropsWithChildren } from "react";

////////////////////////////////////////////////////////////////////////////////////////////////////////
// This is an example of how to use the FormGroupArray component.
// This component is an input component passed from the person component, which is passed from the form.
// It also has FormItem components under it.
// It will create multiple instances of the FormItems for each array in the value passed to it.
// The result will return an object array for each car leases to the person component,
// and the person component will pass it on to its parent component where the form resides.
// ie. [
//       { carLeaseId: 402, make: 'Toyota', model: 'Yaris', fromYear: 2002, toYear: 2008 },
//       { carLeaseId: 404, make: 'Nissan', model: 'Leaf', fromYear: 2008, toYear: 2012 }
//     ]
////////////////////////////////////////////////////////////////////////////////////////////////////////

// allow no members for shorter name to be more readable below
// eslint-disable-next-line
interface T extends CompanyCarLeasesDto { }

export const FormSamplesFormGroupsFGCarLeases = ({
    ...controlProps  // The entire formGroupProps needs to be passed to the FormGroupArray component below
}: FormGroupProps<T>) => {

    const isMobile = isMobileView();

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // We can't create an instance of useForm when using a FormGroupArray as
    // we don't know the form group id (as there's multiple instances for each array).
    // (ie At this level in the component we don't know which instance of the getItem, setItem we need).
    // To overcome this, use the onInstanceInit prop to call the function below.
    // It has the index of the instance as well as an instance of the useForm hook for the instance.
    // (the useForm hook is where you can get the getItem and setItem functions to 
    // get and set items for the array instance the event was raised for).
    //////////////////////////////////////////////////////////////////////////////////////////////////////////////
    const handleOnInstanceInit = (instanceIndex: number, instanceUseForm: UseFormEvent<T>) => {
        const { getItem } = instanceUseForm!;
        const modelField = getItem("model");
        console.log('instance index: ', instanceIndex, 'instance model: ', modelField.value);
    }

    /////////////////////////////////////////////////////////////////////////////////////////
    // As mentioned above we can't create an instance of useForm when using a FormGroupArray.
    // To overcome this for onItemChange and onItemBlur, 
    // use the second parameter to get an instance of the useForm for that instance 
    // as shown below. (where you can get the getItem and setItem to 
    // get and set items for the array instance the event was raised for).
    /////////////////////////////////////////////////////////////////////////////////////////
    const handleMakeChange = (value: FormItemType, useForm: UseFormEvent<T>) => {
        const { getItem } = useForm;
        const modelField = getItem('model');
        console.log(`Make is now '${value as string}', model is currently '${modelField.value as string}'`);
    }

    // Here we are checking that the to year is greater than or equal to the from year.
    // We are doing this on the change and blur events - and that is because when the
    // user is starting to type the to year, it will be less than the from year initially
    // and we don't want the error message to show until after he has completed typing in the year
    // and moved on.
    // If the user comes back to change the field (blur would be true), and then the field
    // should trigger the error check on every change.
    const handleYearChange = (field: keyof T, useForm: UseFormEvent<T>) => {
        const { getItem } = useForm;

        const yearField = getItem(field);
        if (yearField.blurred) {
            checkYearsAreValid(useForm);
        }
    }

    const handleYearBlur = (useForm: UseFormEvent<T>) => {
        checkYearsAreValid(useForm);
    }

    const checkYearsAreValid = (useForm: UseFormEvent<T>) => {
        const { getItem, setError, setMainFormError } = useForm;

        const fromYearField = getItem('fromYear');
        const toYearField = getItem('toYear');

        let valid = true;
        if (Number(toYearField.value) && Number(fromYearField.value)) {
            valid = Number(toYearField.value) >= Number(fromYearField.value);
        }

        // The setError function will show the error in the FormError component defined in this instance
        setError("yearsIncorrect", !valid, "The to year cannot be less than the from year!", ["toYear", "fromYear"]);
        // The setMainFormError will display the error in the FormError component defined in the main form
        setMainFormError!("yearsIncorrect", !valid, "Some to years are less than the from years!");
    }

    // You can customize the confirmation for deleting an item.
    // if nothing is passed then no confirmation is asked when deleting an item.
    const deleteConfirmationText: FormGroupDeleteConfirmationTextProps<T> = {
        message: "Are you sure you want to delete the following:<br /><br />{1} {2}: {3} - {4}",
        fields: ["make", "model", "fromYear", "toYear"]
    };

    const currentYear = new Date().getFullYear();
    const yearRules: FormItemRule<T>[] = [
        { type: "required", message: "Required!" },
        { type: "number", message: "Must be a number!" },
        { type: "minValue", value: 1920, message: "Must be >= 1920!" },
        { type: "maxValue", value: currentYear, message: `Must be <= ${currentYear}!` }
    ];

    return (
        <FGCarLeasesContainer>
            <FormGroupArray<T>
                {...controlProps}  // We need to pass all FormGroupProps passsed into this component
                addItemElement={<AddItem />}
                noItemsElement={<NoItems />}
                deleteConfirmationText={deleteConfirmationText}  // Comment out if you don't want a confirmation on delete
                onInstanceInit={handleOnInstanceInit}
                additionalItems={[{ name: "carLeaseId" }]}
            >
                <Stack
                    direction={isMobile ? 'column' : 'row'}
                    spacing={isMobile ? 0 : 2}
                    sx={isMobile ? { paddingBottom: '14px', borderBottom: '1px solid lightgrey', alignItems: "flex-start" } : {alignItems: "center"}}
                >
                    {/* If you need to display information specific for this iteration of the index, you can use a
                        FormGroupArrayInstance component as shown below. The component is defined below,
                        and has an index as well as a useForm hook to get values of the form from this array iteration.                        
                         */}
                    <FormGroupArrayInstance
                        arrayInstanceElement={<ItemNumber />}
                    />
                    <FormItem<T>
                        formField="make"
                        label="Make"
                        rules={[{ type: "required", message: "Required!" }]}
                        onItemChange={handleMakeChange}
                        element={<TextInput maxLength={20} />}
                    />
                    <FormItem<T>
                        formField="model"
                        label="Model"
                        rules={[{ type: "required", message: "Required!" }]}
                        element={<TextInput maxLength={20} />}
                    />
                    <FormItem<T>
                        formField="fromYear"
                        label="From year"
                        rules={yearRules}
                        onItemChange={(_, useForm) => handleYearChange("fromYear", useForm)}
                        onItemBlur={(_, useForm) => handleYearBlur(useForm)}
                        element={<TextInput maxLength={4} />}
                    />
                    <FormItem<T>
                        formField="toYear"
                        label="To year"
                        rules={yearRules}
                        onItemChange={(_, useForm) => handleYearChange("toYear", useForm)}
                        onItemBlur={(_, useForm) => handleYearBlur(useForm)}
                        element={<TextInput maxLength={4} />}
                    />
                    <Stack direction='row' spacing={0}>
                        <FormGroupArrayAction
                            arrayActiontype="deleteItem"
                            // The element this refers to must have an onClick event as props
                            // It currently is using an element defined in the libaray but you can add
                            // any element that has an onClick and disabled - uncomment below to see
                            // this work with a normal button
                            element={<ActionIconButton
                                tooltip="Delete this lease car" icon={<DeleteIcon />}
                            />}
                        //element={<input type="button" value="delete" />}
                        />
                        <FormGroupArrayAction
                            arrayActiontype="moveItemUp"
                            // The element this refers to must have an onClick event
                            // and a disabled prop to disable if first item.
                            // As mentioned with the delete item this is using an element defined in
                            // the library but you can use the commented out button from the delete
                            // example to show any element can work with the right props
                            element={<ActionIconButton
                                tooltip="Move this item up" icon={<ArrowCircleUpIcon />}
                            />}
                        />
                        <FormGroupArrayAction
                            arrayActiontype="moveItemDown"
                            // The element this refers to must have an onClick event
                            // and a disabled prop to disable if last item.
                            element={<ActionIconButton
                                tooltip="Move this item down" icon={<ArrowCircleDownIcon />}
                            />}
                        />
                    </Stack>
                </Stack>
                {/* We are displaying errors for this form group after the stack (so it's the next line) */}
                <FormErrors forFormErrors />
            </FormGroupArray>
        </FGCarLeasesContainer>
    )
}

// This is the component where you can display specific information for this iteration of the array.
// It has an index and a useForm prop so you can show the relevant information.
// Currently it just shows the row number by writing out the index, but
// you can get the values of the form instance as well, uncomment the relevant sections below
// to display the id instead of the row index.
const ItemNumber = ({ instanceIndex, instanceUseForm }: FormGroupArrayInstanceProps<T>) => {
    const { getItem } = instanceUseForm!;
    // This comment is to get rid of es lint warning that idField isn't used, it's there to show as an example
    // eslint-disable-next-line     
    const idField = getItem("carLeaseId");
    return (
        <Box sx={{ width: '20px', fontSize: '12px' }}>
            {/* {idField.value ? idField.value?.toString() : 'new'}. */}
            {instanceIndex! + 1}.
        </Box>
    )
}

const FGCarLeasesContainer = ({ children }: PropsWithChildren) => {

    const theme = useTheme();

    return (
        <Box
            sx={{ marginTop: 2, padding: '10px', borderRadius: 2, background: theme.palette.grey[300] }}
        >
            <Box color={theme.palette.text.secondary} sx={{ padding: '4px 0 10px 0', fontSize: "small" }}>
                Car Leases
            </Box>
            <Paper sx={{ padding: '10px' }}>
                {children}
            </Paper>
        </Box>
    )
}

const AddItem = ({ onAddItem }: FormGroupArrayAddItemProps) => {
    return (
        <Box>
            <ActionIconButton
                tooltip="Add new lease car" icon={<AddCircleIcon />} onClick={onAddItem!}
            />
        </Box>
    )
}

const NoItems = () => {
    return (
        <Box sx={{ color: 'lightgrey' }}>There are no car leases for this person.</Box>
    )
}
